import { Button, Checkbox, Col, DatePicker, Form, Input, InputNumber, Modal, Result, Row, Select, Skeleton, Tabs, Card } from "antd";
import React, { useEffect, useState } from "react";
import { Asset as AssetEntity, AssetType, PowerCurve } from "../data/Asset";
import { Park } from "../data/Park";
import { Portfolio } from "../data/Portfolio";
import { SyncOutlined } from "@ant-design/icons";
import { useHistory } from "react-router";
import TextArea from "antd/lib/input/TextArea";
import moment from "moment";

const { Option } = Select;
const { TabPane } = Tabs;

const Asset: React.FC<{ id: string }> = (props) => {
    const [portfolios, setPortfolios] = useState<Array<Portfolio>>([]);
    const [parks, setParks] = useState<Array<Park>>([]);
    const [asset, setAsset] = useState<AssetEntity | null>(null);
    const [loadingState, setLoadingState] = useState<{ loading: boolean; hasError: boolean }>({ loading: false, hasError: false });
    const [parkModalState, setParkModalState] = useState<{ visible: boolean; loading: boolean }>({ loading: false, visible: false });
    const [portfolioModalState, setPortfolioModalState] = useState<{ visible: boolean; loading: boolean }>({ loading: false, visible: false });
    const [assetType, setAssetType] = useState<AssetType>("Other");
    const [powerCurves, setPowerCurves] = useState<PowerCurve[]>([{ label: "", count: 1, hubHeight: 100, powerCurveData: "" }]);
    const [activePowerCurveTab, setActivePowerCurveTab] = useState<string>("0");
    const [powerCurveError, setPowerCurveError] = useState<string | null>(null);
    const history = useHistory();

    const renderParksSelect = () => {
        return (
            <Select optionFilterProp="children" autoClearSearchValue={false} showSearch={true} allowClear style={{ width: "100%" }} placeholder="Select park">
                <Option key={0} value={0}>
                    No park
                </Option>
                {parks.map((x) => (
                    <Option key={x.id} value={x.id}>
                        {x.name}
                    </Option>
                ))}
            </Select>
        );
    };

    const renderPortfoliosSelect = () => {
        return (
            <Select
                mode="multiple"
                optionFilterProp="children"
                autoClearSearchValue={false}
                showSearch={true}
                allowClear
                style={{ width: "100%" }}
                placeholder="Select portfolios"
            >
                {portfolios.map((x) => (
                    <Option key={x.id} value={x.id}>
                        {x.name}
                    </Option>
                ))}
            </Select>
        );
    };

    const loadAsset = async () => {
        const assetResp = await fetch(`/api/Assets/${props.id}`, {
            method: "GET",
            headers: {
                Accept: "application/json",
                "Content-type": "application/json"
            }
        });

        if (assetResp.status === 200) {
            const a = (await assetResp.json()) as AssetEntity;
            setAsset(a);
            setAssetType(a.type);

            if (a.type === "WindTurbine" && a.powerCurves && a.powerCurves.length > 0) {
                setPowerCurves(a.powerCurves);
            }

            document.title = `Edit ${a.name}`;
            return true;
        }

        return false;
    };

    const loadParks = async () => {
        const parksResp = await fetch(`/api/Parks`, {
            method: "GET",
            headers: {
                Accept: "application/json",
                "Content-type": "application/json"
            }
        });

        if (parksResp.status === 200) {
            setParks(await parksResp.json());
            return true;
        }

        return false;
    };

    const loadPortfolios = async () => {
        const portfoliosResp = await fetch(`/api/Portfolios`, {
            method: "GET",
            headers: {
                Accept: "application/json",
                "Content-type": "application/json"
            }
        });

        if (portfoliosResp.status === 200) {
            setPortfolios(await portfoliosResp.json());
            return true;
        }

        return false;
    };

    const loadData = async () => {
        setLoadingState({ loading: true, hasError: false });

        if (props.id !== "") {
            const d1 = await loadAsset();
            if (!d1) {
                setLoadingState({ loading: false, hasError: true });
                return;
            }
        }

        const d2 = await loadParks();
        if (!d2) {
            setLoadingState({ loading: false, hasError: true });
            return;
        }

        const d3 = await loadPortfolios();
        if (!d3) {
            setLoadingState({ loading: false, hasError: true });
            return;
        }

        setLoadingState({ loading: false, hasError: false });
    };

    const onSaveParkClick = async (values: any) => {
        setParkModalState({ visible: true, loading: true });

        const parkResp = await fetch(`/api/Parks`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-type": "application/json"
            },
            body: JSON.stringify({
                name: values["name"]
            })
        });

        if (parkResp.status === 201) {
            await loadParks();
            setParkModalState({ loading: false, visible: false });
            return;
        }

        setParkModalState({ loading: false, visible: false });
        setLoadingState({ loading: false, hasError: true });
    };

    const onSavePortfolioClick = async (values: any) => {
        setPortfolioModalState({ visible: true, loading: true });

        const portfolioResp = await fetch(`/api/Portfolios`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-type": "application/json"
            },
            body: JSON.stringify({
                name: values["name"]
            })
        });

        if (portfolioResp.status === 201) {
            await loadPortfolios();
            setPortfolioModalState({ loading: false, visible: false });
            return;
        }

        setPortfolioModalState({ loading: false, visible: false });
        setLoadingState({ loading: false, hasError: true });
    };

    const onFieldsChange = (changedFields: any, allFields: any) => {
        setAssetType(allFields[1].value);
    };

    const toDateOnly = (value: any): string | null => {
        if (!value) {
            return null;
        }

        // Take the date the user picked (as displayed) and mark it as UTC midnight.
        // We intentionally do NOT convert the time - e.g. 2026-07-01 stays 2026-07-01T00:00:00Z.
        return moment(value).format("YYYY-MM-DD") + "T00:00:00.000Z";
    };

    const addPowerCurve = () => {
        const newCurves = [...powerCurves, { label: "", count: 1, hubHeight: 100, powerCurveData: "" }];
        setPowerCurves(newCurves);
        setActivePowerCurveTab((newCurves.length - 1).toString());
    };

    const removePowerCurve = (index: number) => {
        if (powerCurves.length === 1) {
            return;
        }
        const newCurves = powerCurves.filter((_, i) => i !== index);
        setPowerCurves(newCurves);
        setActivePowerCurveTab("0");
    };

    const updatePowerCurve = (index: number, field: keyof PowerCurve, value: any) => {
        const newCurves = [...powerCurves];
        newCurves[index] = { ...newCurves[index], [field]: value };
        setPowerCurves(newCurves);
        if (field === "powerCurveData" && value && value.trim().length > 0) {
            setPowerCurveError(null);
        }
    };

    const validatePowerCurveData = (data: string): boolean => {
        if (!data || data.trim().length === 0) {
            return false;
        }

        const lines = data
            .split("\n")
            .map((line: string) => line.trim())
            .filter((line: string) => line.length > 0);
        const regex = /^(\d+(\.\d+)?)\s+(\d+(\.\d+)?)$/;
        return lines.every((line: string) => regex.test(line));
    };

    const renderPowerCurveTabs = () => {
        return (
            <div style={{ marginBottom: "24px" }}>
                <Tabs
                    type="editable-card"
                    activeKey={activePowerCurveTab}
                    onChange={setActivePowerCurveTab}
                    onEdit={(targetKey, action) => {
                        if (action === "add") {
                            addPowerCurve();
                        } else if (action === "remove") {
                            removePowerCurve(parseInt(targetKey as string));
                        }
                    }}
                >
                    {powerCurves.map((curve, index) => (
                        <TabPane tab={curve.label || `Curve ${index + 1}`} key={index.toString()} closable={powerCurves.length > 1}>
                            <Card>
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <label>Label (optional)</label>
                                        <Input
                                            placeholder="e.g. Vestas V150"
                                            value={curve.label}
                                            onChange={(e) => updatePowerCurve(index, "label", e.target.value)}
                                            maxLength={100}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        <label>
                                            Number of turbines <span style={{ color: "red" }}>*</span>
                                        </label>
                                        <InputNumber
                                            placeholder="e.g. 10"
                                            value={curve.count}
                                            onChange={(value) => updatePowerCurve(index, "count", value ?? 1)}
                                            min={1}
                                            style={{ width: "100%" }}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        <label>
                                            Hub height (meters) <span style={{ color: "red" }}>*</span>
                                        </label>
                                        <InputNumber
                                            placeholder="e.g. 100"
                                            value={curve.hubHeight}
                                            onChange={(value) => updatePowerCurve(index, "hubHeight", value ?? 100)}
                                            min={10}
                                            style={{ width: "100%" }}
                                            status={!curve.hubHeight || curve.hubHeight <= 0 ? "error" : undefined}
                                        />
                                        {(!curve.hubHeight || curve.hubHeight <= 0) && (
                                            <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>Hub height is required</div>
                                        )}
                                    </Col>
                                </Row>
                                <Row style={{ marginTop: "16px" }}>
                                    <Col span={24}>
                                        <label>
                                            Power Curve Data <span style={{ color: "red" }}>*</span>
                                        </label>
                                        <div style={{ fontSize: "12px", color: "#888", marginBottom: "8px" }}>
                                            Enter the power curve in the format: wind speed (m/s) followed by power (kW), one pair per line.
                                        </div>
                                        <TextArea
                                            rows={8}
                                            placeholder={`0 0\n3 100\n3.5 150\n4 200\n4.5 250`}
                                            value={curve.powerCurveData}
                                            onChange={(e) => updatePowerCurve(index, "powerCurveData", e.target.value)}
                                            status={curve.powerCurveData && !validatePowerCurveData(curve.powerCurveData) ? "error" : undefined}
                                        />
                                        {curve.powerCurveData && !validatePowerCurveData(curve.powerCurveData) && (
                                            <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                                                Invalid power curve format. Please check the format and try again.
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                            </Card>
                        </TabPane>
                    ))}
                </Tabs>
            </div>
        );
    };

    const onSaveClick = async (values: any) => {
        if (values["type"] === "WindTurbine" || assetType === "WindTurbine") {
            const hasMissingData = powerCurves.some((c) => !c.powerCurveData || c.powerCurveData.trim().length === 0);
            if (hasMissingData) {
                setPowerCurveError("Please add power curve data for all power curves.");
                return;
            }
            setPowerCurveError(null);
        }

        setLoadingState({ loading: true, hasError: false });
        let parkId = values["park-id"];
        if (parkId === undefined || parkId === 0) {
            parkId = null;
        }

        const portfolioIds: number[] = values["portfolio-ids"] ?? [];

        const url = props.id === "" ? `/api/Assets` : `/api/Assets/${props.id}`;
        const method = props.id === "" ? "POST" : "PUT";
        const assetId = props.id === "" ? 0 : parseInt(props.id);

        const atype: AssetType = values["type"];

        const requestBody: any = {
            name: values["name"],
            parkId: parkId,
            portfolios: portfolioIds.map((id) => ({ id, name: "dummy" })),
            id: assetId,
            type: atype,
            externalId: values["externalId"],
            longitude: values["longitude"],
            latitude: values["latitude"],
            capacity: values["capacity"],
            capacityCap: values["capacityCap"],
            surfaceTilt: values["surfaceTilt"],
            eastTiltFrom: values["eastTiltFrom"],
            westTiltTo: values["westTiltTo"],
            azimuthFrom: values["azimuthFrom"],
            azimuthTo: values["azimuthTo"],
            hubHeight: values["hubHeight"],
            sweptArea: values["sweptArea"] ?? 0,
            bifacial: values["bifacial"],
            activeFrom: toDateOnly(values["activeFrom"]),
            activeTo: toDateOnly(values["activeTo"])
        };

        if (atype === "WindTurbine") {
            requestBody.powerCurves = powerCurves;
        }

        const assetResp = await fetch(url, {
            method: method,
            headers: {
                Accept: "application/json",
                "Content-type": "application/json"
            },
            body: JSON.stringify(requestBody)
        });

        if (assetResp.status >= 400) {
            setLoadingState({ loading: false, hasError: true });
            return;
        }

        history.push("/assets");
    };

    const renderForm = () => {
        return (
            <Form
                name="create-edit-asset"
                labelCol={{ span: 4 }}
                wrapperCol={{ span: 8 }}
                layout="horizontal"
                initialValues={{
                    name: asset?.name,
                    type: asset?.type,
                    externalId: asset?.externalId,
                    longitude: asset?.longitude,
                    latitude: asset?.latitude,
                    capacity: asset?.capacity,
                    capacityCap: asset?.capacityCap,
                    surfaceTilt: asset?.surfaceTilt,
                    eastTiltFrom: asset?.eastTiltFrom,
                    westTiltTo: asset?.westTiltTo,
                    azimuthFrom: asset?.azimuthFrom,
                    azimuthTo: asset?.azimuthTo,
                    bifacial: asset?.bifacial,
                    activeFrom: asset?.activeFrom ? moment(asset.activeFrom) : null,
                    activeTo: asset?.activeTo ? moment(asset.activeTo) : null,
                    "park-id": asset?.parkId,
                    "portfolio-ids": asset?.portfolios?.map((x) => x.id) ?? [] // changed
                }}
                onFieldsChange={onFieldsChange}
                onFinish={onSaveClick}
            >
                <h3>{props.id === "" ? "Create" : "Edit"} asset</h3>

                <Form.Item label="Name" name="name" rules={[{ required: true, message: "Please enter the name of the asset." }]}>
                    <Input placeholder="e.g. Wind Turbine 1" />
                </Form.Item>

                <Form.Item label="Type" name="type" rules={[{ required: true, message: "Please select the type of the asset." }]}>
                    <Select>
                        <Option key={"Other"}>Other</Option>
                        <Option key={"WindTurbine"}>Wind turbine or park</Option>
                        <Option key={"PhotovoltaicArray"}>PV park</Option>
                    </Select>
                </Form.Item>

                <Row>
                    <Col span={6}>
                        <Form.Item
                            labelCol={{ span: 16 }}
                            label="Longitude"
                            name="longitude"
                            rules={[{ required: true, message: "Please enter the longitude of the asset.", type: "number" }]}
                        >
                            <InputNumber placeholder="e.g. 20" controls={false} style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item
                            labelCol={{ span: 6 }}
                            label="Latitude"
                            name="latitude"
                            rules={[{ required: true, message: "Please enter the latitude of the asset.", type: "number", min: -360, max: 360 }]}
                        >
                            <InputNumber placeholder="e.g. 30" controls={false} style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item
                    label="Capacity KW"
                    name="capacity"
                    rules={[{ required: true, message: "Please enter the capacity of the asset in KW.", type: "number", min: 0 }]}
                >
                    <InputNumber placeholder="e.g. 1000kW" controls={false} />
                </Form.Item>

                <Form.Item label="Capacity Cap KW" name="capacityCap" rules={[{ required: false, type: "number", min: 0 }]}>
                    <InputNumber placeholder="e.g. 1000kW" controls={false} />
                </Form.Item>
                <Form.Item
                    hidden={assetType !== "PhotovoltaicArray"}
                    label="Surface tilt &deg;"
                    name="surfaceTilt"
                    rules={[
                        {
                            required: assetType === "PhotovoltaicArray",
                            message: "Please enter the surface tilt of the asset in degrees.",
                            type: "number",
                            min: 0
                        }
                    ]}
                >
                    <InputNumber placeholder="e.g. 25" controls={false} />
                </Form.Item>

                <Row hidden={assetType !== "PhotovoltaicArray"}>
                    <Col offset={4}>For fixed tilt panels (no tilt tracking), enter zero in both fields.</Col>
                </Row>
                <Row hidden={assetType !== "PhotovoltaicArray"}>
                    <Col span={6}>
                        <Form.Item
                            labelCol={{ span: 16 }}
                            label="East tilt range from &deg;"
                            name="eastTiltFrom"
                            rules={[{ required: assetType === "PhotovoltaicArray", message: "Please enter the min tilt.", type: "number", min: -90, max: 90 }]}
                        >
                            <InputNumber placeholder="e.g. -60" controls={false} style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item
                            labelCol={{ span: 6 }}
                            label="to west &deg;"
                            name="westTiltTo"
                            rules={[{ required: assetType === "PhotovoltaicArray", message: "Please enter the max tilt.", type: "number", min: -90, max: 90 }]}
                        >
                            <InputNumber placeholder="e.g. 60" controls={false} style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row hidden={assetType !== "PhotovoltaicArray"}>
                    <Col offset={4}>For fixed azimuth panels (no azimuth tracking), enter the same value in both fields — usually 180° for south-facing.</Col>
                </Row>
                <Row hidden={assetType !== "PhotovoltaicArray"}>
                    <Col span={6}>
                        <Form.Item
                            labelCol={{ span: 16 }}
                            label="Azimuth range from &deg;"
                            name="azimuthFrom"
                            rules={[{ type: "number", min: -360, max: 360 }]}
                        >
                            <InputNumber placeholder="e.g. 180" controls={false} style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item labelCol={{ span: 6 }} label="to &deg;" name="azimuthTo" rules={[{ type: "number", min: -360, max: 360 }]}>
                            <InputNumber placeholder="e.g. 180" controls={false} style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item hidden={assetType !== "PhotovoltaicArray"} label="Bifacial" valuePropName="checked" name="bifacial">
                    <Checkbox></Checkbox>
                </Form.Item>

                {assetType === "WindTurbine" && (
                    <Form.Item label="Power Curves" required validateStatus={powerCurveError ? "error" : undefined} help={powerCurveError}>
                        {renderPowerCurveTabs()}
                    </Form.Item>
                )}

                <Form.Item label="External ID" name="externalId" rules={[{ max: 100 }]}>
                    <Input placeholder="e.g. EXT123" maxLength={100} />
                </Form.Item>

                <Form.Item label="Active from" name="activeFrom">
                    <DatePicker allowClear format="YYYY-MM-DD" style={{ width: "100%" }} />
                </Form.Item>

                <Form.Item label="Active to" name="activeTo">
                    <DatePicker allowClear format="YYYY-MM-DD" style={{ width: "100%" }} />
                </Form.Item>

                <Form.Item label="Park">
                    <Row>
                        <Col span={16}>
                            <Form.Item style={{ marginBottom: "0px" }} name="park-id">
                                {renderParksSelect()}
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Button block={true} type="primary" icon={<SyncOutlined />} onClick={loadParks}></Button>
                        </Col>
                    </Row>
                </Form.Item>

                {/* <Form.Item label="Portfolio">
                    <Row>
                        <Col span={16}>
                            <Form.Item style={{ marginBottom: "0px" }} name="portfolio-id">
                                {renderPortfoliosSelect()}
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Button block={true} type="primary" icon={<SyncOutlined />} onClick={loadPortfolios}></Button>
                        </Col>
                    </Row>
                </Form.Item> */}
                <Form.Item label="Portfolio">
                    <Row>
                        <Col span={16}>
                            <Form.Item style={{ marginBottom: "0px" }} name="portfolio-ids">
                                {renderPortfoliosSelect()}
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Button block={true} type="primary" icon={<SyncOutlined />} onClick={loadPortfolios}></Button>
                        </Col>
                    </Row>
                </Form.Item>
                <Row>
                    <Col offset={8} span={4}>
                        <Button disabled={loadingState.loading} type="primary" htmlType="submit">
                            Save
                        </Button>
                        <Button
                            disabled={loadingState.loading}
                            htmlType="button"
                            onClick={() => {
                                history.push("/assets");
                            }}
                        >
                            Cancel
                        </Button>
                    </Col>
                </Row>
            </Form>
        );
    };

    useEffect(() => {
        loadData();
        if (props.id === "") {
            document.title = `Create asset`;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {loadingState.loading && <Skeleton />}
            {!loadingState.loading && !loadingState.hasError && renderForm()}
            {!loadingState.loading && loadingState.hasError && (
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Result
                            status="error"
                            title="Unable to contact server"
                            subTitle="Please check your connectivity and try again."
                            extra={[
                                <Button type="primary" key="console" onClick={loadData}>
                                    Retry
                                </Button>
                            ]}
                        ></Result>
                    </Col>
                </Row>
            )}
            <Modal title="Add park" visible={parkModalState.visible} confirmLoading={parkModalState.loading} destroyOnClose={true} footer={null}>
                <Form name="add-park" wrapperCol={{ span: 24 }} layout="horizontal" initialValues={{ name: "" }} onFinish={onSaveParkClick}>
                    <Form.Item name="name" rules={[{ required: true, message: "Please enter the name of the park." }]}>
                        <Input placeholder="E.g. PV park 1" />
                    </Form.Item>
                    <Row>
                        <Col span={24} style={{ textAlign: "right" }}>
                            <Button loading={parkModalState.loading} type="primary" htmlType="submit">
                                Save
                            </Button>
                            <Button
                                disabled={parkModalState.loading}
                                htmlType="button"
                                onClick={() => {
                                    setParkModalState({ loading: false, visible: false });
                                }}
                            >
                                Cancel
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            <Modal title="Add portfolio" visible={portfolioModalState.visible} confirmLoading={portfolioModalState.loading} destroyOnClose={true} footer={null}>
                <Form name="add-portfolio" wrapperCol={{ span: 24 }} layout="horizontal" initialValues={{ name: "" }} onFinish={onSavePortfolioClick}>
                    <Form.Item name="name" rules={[{ required: true, message: "Please enter the name of the portfolio." }]}>
                        <Input placeholder="E.g. Operational" />
                    </Form.Item>
                    <Row>
                        <Col span={24} style={{ textAlign: "right" }}>
                            <Button loading={portfolioModalState.loading} type="primary" htmlType="submit">
                                Save
                            </Button>
                            <Button
                                disabled={portfolioModalState.loading}
                                htmlType="button"
                                onClick={() => {
                                    setPortfolioModalState({ loading: false, visible: false });
                                }}
                            >
                                Cancel
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </>
    );
};

export default Asset;
